import { useState } from "react";
import { useLocation } from "wouter";
import {
  useListPendingRecommendations,
  useAcknowledgeRecommendedPrice,
  getListPendingRecommendationsQueryKey,
  getListInventoryQueryKey,
  getGetDashboardSummaryQueryKey,
  useGetMe,
  type InventoryListing,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatMoney } from "./inventory-cells";
import { cn } from "@/lib/utils";

function fmtSetMeta(by?: string | null, at?: string | null): string | null {
  const parts: string[] = [];
  if (by) parts.push(by.split("@")[0]);
  if (at) {
    const d = new Date(at);
    if (!Number.isNaN(d.getTime())) {
      parts.push(
        d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      );
    }
  }
  return parts.length ? parts.join(" · ") : null;
}

function seatLabel(row: InventoryListing): string {
  const bits = [row.section, row.row].filter(Boolean);
  return bits.length ? bits.join(" · ") : "—";
}

// In-app notification surface for pending recommended-price changes. A puller
// gets a badge count of listings whose recommended price an admin set but they
// have not yet acknowledged — without needing to be on the affected row. Each
// item jumps to the listing (via the home global-search deep-link) and can be
// acknowledged in place, clearing the count.
export function RecommendationsBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";

  const { data: pending } = useListPendingRecommendations({
    query: {
      queryKey: getListPendingRecommendationsQueryKey(),
      // Poll so a puller learns about a new request without a manual reload.
      refetchInterval: 60_000,
    },
  });

  const items = pending ?? [];
  const count = items.length;

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getListPendingRecommendationsQueryKey(),
    });
    queryClient.invalidateQueries({ queryKey: getListInventoryQueryKey() });
    queryClient.invalidateQueries({
      queryKey: getGetDashboardSummaryQueryKey(),
    });
    // Event-scoped inventory queries are keyed per event; refresh them all so an
    // acknowledged row's amber indicator clears in any open table too.
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        q.queryKey[0].includes("/inventory"),
    });
  };

  const acknowledge = useAcknowledgeRecommendedPrice({
    mutation: { onSuccess: invalidate },
  });

  const jumpTo = (row: InventoryListing) => {
    setOpen(false);
    if (row.reachListingId) {
      setLocation(`/?focus=${encodeURIComponent(row.reachListingId)}`);
    } else {
      setLocation("/");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/*
        Use a SINGLE Radix trigger on this button. Previously this nested
        `<TooltipTrigger asChild><PopoverTrigger asChild><button>`, which
        composes two Radix trigger refs onto the same DOM node. Under React 19
        that ref composition (@radix-ui/react-compose-refs) can churn on mount
        and throw React #185 ("maximum update depth exceeded"), crashing the
        whole authenticated app from the header. The hover hint is preserved via
        a native `title`; the button keeps its `aria-label`.
      */}
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            count > 0
              ? `${count} pending recommended-price change${count === 1 ? "" : "s"}`
              : "Recommended-price requests"
          }
          title={
            count > 0
              ? `${count} recommended-price ${count === 1 ? "change" : "changes"} to review`
              : "No pending recommended-price changes"
          }
          className={cn(
            "relative flex h-7 w-7 items-center justify-center rounded-md cursor-pointer transition-colors",
            count > 0
              ? "text-amber-400 hover:bg-amber-500/10"
              : "text-secondary-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-amber-500 text-[9px] font-mono font-bold leading-none text-background">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-xs font-semibold text-foreground">
            Recommended price requests
          </div>
          <p className="text-[10px] text-muted-foreground">
            {isAdmin
              ? "Pending changes awaiting puller acknowledgment."
              : "New list prices an admin asked you to apply."}
          </p>
        </div>

        {count === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            You're all caught up.
          </div>
        ) : (
          <ScrollArea className="max-h-80">
            <ul className="divide-y divide-border">
              {items.map((row) => {
                const meta = fmtSetMeta(
                  row.recommendedListPriceSetBy,
                  row.recommendedListPriceSetAt,
                );
                const acking =
                  acknowledge.isPending &&
                  acknowledge.variables?.id === row.id;
                return (
                  <li key={row.id} className="px-3 py-2">
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => jumpTo(row)}
                        className="flex-1 min-w-0 text-left group/item"
                        title="Jump to listing"
                      >
                        <div className="text-xs font-medium text-foreground truncate group-hover/item:underline">
                          {row.eventName ?? "Unknown event"}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {seatLabel(row)}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <span className="font-mono text-xs text-amber-400">
                            {formatMoney(
                              row.recommendedListPrice,
                              row.currencyCode,
                            )}
                          </span>
                          {meta && (
                            <span className="text-[10px] text-muted-foreground truncate">
                              · {meta}
                            </span>
                          )}
                        </div>
                      </button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            disabled={acking}
                            onClick={() =>
                              acknowledge.mutate({ id: row.id })
                            }
                            aria-label="Acknowledge recommended price change"
                            className="shrink-0 flex h-7 w-7 items-center justify-center rounded-sm text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
                          >
                            {acking ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Acknowledge</TooltipContent>
                      </Tooltip>
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
